import { useMemo, useState } from 'react';
import { Code2, Search, GitBranch, Terminal, ChevronDown, ChevronRight, Play, Database } from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName } from '@har-insight/parser';
import type { HAREntry } from '@har-insight/parser';
import { cn } from '@/lib/utils';

interface GraphQLRequest {
  entry: HAREntry;
  operationType: 'query' | 'mutation' | 'subscription';
  operationName: string;
  query: string;
  variables?: object;
  complexity: 'low' | 'medium' | 'high';
}

interface GraphQLInsight {
  type: 'error' | 'warning' | 'info';
  icon: React.ReactNode;
  title: string;
  description: string;
  requests?: GraphQLRequest[];
}

export function GraphQLAnalysis() {
  const { filteredEntries, selectEntry } = useHARStore();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  const graphqlRequests = useMemo(() => {
    const requests: GraphQLRequest[] = [];

    filteredEntries.forEach(entry => {
      // Check if it's a GraphQL request by URL or content type
      const isGraphQLUrl = entry.request.url.toLowerCase().includes('graphql');
      const isGraphQLContent = entry.request.postData?.text?.includes('query') ||
                               entry.request.postData?.text?.includes('mutation');

      if ((isGraphQLUrl || isGraphQLContent) && entry.request.postData?.text) {
        try {
          const payload = JSON.parse(entry.request.postData.text);
          const query = payload.query || payload.extensions?.persistedQuery?.sha256Hash;
          
          if (query) {
            const operationType = determineOperationType(query);
            const operationName = extractOperationName(query) || 'Anonymous';
            const complexity = analyzeComplexity(query);

            requests.push({
              entry,
              operationType,
              operationName,
              query,
              variables: payload.variables,
              complexity,
            });
          }
        } catch {
          // Not valid JSON, skip
        }
      }
    });

    return requests;
  }, [filteredEntries]);

  const insights = useMemo(() => {
    if (graphqlRequests.length === 0) return [];

    const result: GraphQLInsight[] = [];

    // Complex queries
    const complexQueries = graphqlRequests.filter(r => r.complexity === 'high');
    if (complexQueries.length > 0) {
      result.push({
        type: 'warning',
        icon: <Search className="w-4 h-4" />,
        title: `${complexQueries.length} complex GraphQL ${complexQueries.length === 1 ? 'operation' : 'operations'}`,
        description: 'Complex queries may impact server performance. Consider pagination or breaking into smaller queries.',
        requests: complexQueries.slice(0, 5),
      });
    }

    // Slow GraphQL requests
    const slowRequests = graphqlRequests.filter(r => r.entry.time > 1000);
    if (slowRequests.length > 0) {
      result.push({
        type: 'warning',
        icon: <Terminal className="w-4 h-4" />,
        title: `${slowRequests.length} slow GraphQL ${slowRequests.length === 1 ? 'request' : 'requests'}`,
        description: 'GraphQL requests taking over 1 second. Consider query optimization or server-side caching.',
        requests: slowRequests.slice(0, 5),
      });
    }

    // Large payloads
    const largePayloads = graphqlRequests.filter(r => 
      (r.entry.response.content.size || 0) > 100 * 1024
    );
    if (largePayloads.length > 0) {
      result.push({
        type: 'info',
        icon: <Database className="w-4 h-4" />,
        title: `${largePayloads.length} large GraphQL ${largePayloads.length === 1 ? 'response' : 'responses'}`,
        description: 'Responses exceeding 100KB. Consider pagination or field selection.',
        requests: largePayloads.slice(0, 5),
      });
    }

    // Anonymous operations
    const anonymousOps = graphqlRequests.filter(r => r.operationName === 'Anonymous');
    if (anonymousOps.length > 0) {
      result.push({
        type: 'info',
        icon: <Code2 className="w-4 h-4" />,
        title: `${anonymousOps.length} anonymous ${anonymousOps.length === 1 ? 'operation' : 'operations'}`,
        description: 'Consider naming your GraphQL operations for better debugging and logging.',
        requests: anonymousOps.slice(0, 5),
      });
    }

    return result;
  }, [graphqlRequests]);

  const groupedRequests = useMemo(() => {
    const groups: Record<string, GraphQLRequest[]> = {};
    
    graphqlRequests.forEach(req => {
      const key = `${req.operationType}-${req.operationName}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(req);
    });

    return groups;
  }, [graphqlRequests]);

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (graphqlRequests.length === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-3">
          <GitBranch className="w-6 h-6 text-slate-400" />
        </div>
        <h3 className="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">No GraphQL requests found</h3>
        <p className="text-xs text-slate-500">This HAR file doesn't contain any detectable GraphQL operations.</p>
      </div>
    );
  }

  return (
    <div className="card p-4 h-[450px] flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 -mr-1 space-y-4">
        {/* Insights */}
        {insights.length > 0 && (
          <div className="space-y-2">
            {insights.map((insight, index) => (
              <div
                key={index}
                className={cn(
                  'p-3 rounded-lg border',
                  insight.type === 'error' && 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30',
                  insight.type === 'warning' && 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/30',
                  insight.type === 'info' && 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/30'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn(
                    'p-1.5 rounded',
                    insight.type === 'error' && 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400',
                    insight.type === 'warning' && 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400',
                    insight.type === 'info' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                  )}>
                    {insight.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={cn(
                      'text-sm font-medium',
                      insight.type === 'error' && 'text-red-700 dark:text-red-400',
                      insight.type === 'warning' && 'text-yellow-700 dark:text-yellow-400',
                      insight.type === 'info' && 'text-blue-700 dark:text-blue-400'
                    )}>
                      {insight.title}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                      {insight.description}
                    </p>
                    {insight.requests && insight.requests.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {insight.requests.map((req, i) => (
                          <button
                            key={i}
                            onClick={() => selectEntry(req.entry)}
                            className="w-full text-left text-xs p-1.5 rounded bg-white/50 dark:bg-slate-900/30 hover:bg-white dark:hover:bg-slate-900/50 transition-colors flex items-center justify-between gap-2"
                          >
                            <span className="truncate text-slate-700 dark:text-slate-300">
                              {req.operationName}
                            </span>
                            <span className="flex-shrink-0 text-slate-500">
                              {formatTime(req.entry.time)}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Grouped Operations */}
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
            Operations by Type
          </h3>
          {Object.entries(groupedRequests).map(([key, requests]) => {
            const firstReq = requests[0];
            const isExpanded = expandedGroups[key];

            return (
              <div key={key} className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleGroup(key)}
                  className="w-full flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {firstReq.operationType === 'query' && <Search className="w-4 h-4 text-blue-500" />}
                    {firstReq.operationType === 'mutation' && <Play className="w-4 h-4 text-orange-500" />}
                    {firstReq.operationType === 'subscription' && <GitBranch className="w-4 h-4 text-green-500" />}
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {firstReq.operationName}
                    </span>
                    <span className={cn(
                      'text-[10px] px-1.5 py-0.5 rounded uppercase font-bold',
                      firstReq.operationType === 'query' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
                      firstReq.operationType === 'mutation' && 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-300',
                      firstReq.operationType === 'subscription' && 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300'
                    )}>
                      {firstReq.operationType}
                    </span>
                    {firstReq.complexity === 'high' && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300 uppercase font-bold">
                        Complex
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">{requests.length}×</span>
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>
                {isExpanded && (
                  <div className="p-2 space-y-1 bg-white dark:bg-slate-900">
                    {requests.map((req, i) => (
                      <button
                        key={i}
                        onClick={() => selectEntry(req.entry)}
                        className="w-full text-left text-xs p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="truncate text-slate-700 dark:text-slate-300">
                            {getFileName(req.entry.request.url)}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={cn(
                              'text-[10px] px-1.5 py-0.5 rounded',
                              req.entry.response.status < 400 ? 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-300' : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'
                            )}>
                              {req.entry.response.status}
                            </span>
                            <span className="text-slate-500">{formatTime(req.entry.time)}</span>
                            <span className="text-slate-500">{formatBytes(req.entry.response.content.size || 0)}</span>
                          </div>
                        </div>
                        <div className="mt-1 text-[10px] text-slate-500 font-mono truncate">
                          {req.query.substring(0, 100)}...
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function determineOperationType(query: string): 'query' | 'mutation' | 'subscription' {
  const trimmed = query.trim().toLowerCase();
  if (trimmed.startsWith('mutation')) return 'mutation';
  if (trimmed.startsWith('subscription')) return 'subscription';
  return 'query';
}

function extractOperationName(query: string): string | null {
  // Match query/mutation/subscription Name { or Name(
  const match = query.match(/(?:query|mutation|subscription)\s+(\w+)/i);
  return match?.[1] || null;
}

function analyzeComplexity(query: string): 'low' | 'medium' | 'high' {
  const depth = calculateQueryDepth(query);
  const fieldCount = (query.match(/\{/g) || []).length;
  
  if (depth > 5 || fieldCount > 30) return 'high';
  if (depth > 3 || fieldCount > 15) return 'medium';
  return 'low';
}

function calculateQueryDepth(query: string): number {
  let maxDepth = 0;
  let currentDepth = 0;
  
  for (const char of query) {
    if (char === '{') {
      currentDepth++;
      maxDepth = Math.max(maxDepth, currentDepth);
    } else if (char === '}') {
      currentDepth--;
    }
  }
  
  return maxDepth;
}
